import numeral from 'numeral';

import type { IGoal } from '@feathr/blackbox';
import type { ISorted } from '@feathr/components';
import { moment, TimeFormat } from '@feathr/hooks';

import type { TConversionTrackingModel } from './ConversionTableCard.types';

export const CONVERSION_TRACKING_TOOLTIPS: Record<TConversionTrackingModel, string> = {
  Single:
    'Only includes the first conversion of each person, even if they converted more than once.',
  Multi: 'Includes multiple conversions by the same person if converted more than once.',
  Mixed:
    'Includes conversions from goals set up to track both single and multiple conversions per person.',
};

export function getConversionTrackingModel(goals: IGoal[]): TConversionTrackingModel | undefined {
  if (goals.length < 1) {
    return undefined;
  }

  const modes = [...new Set(goals.map((goal) => goal.multi_conversions))];

  if (modes.length === 1) {
    return modes[0] ? 'Multi' : 'Single';
  }

  return 'Mixed';
}

export function addDayToDate(dateString: string): string {
  const isoDatePattern = /^\d{4}-\d{2}-\d{2}$/;

  if (!isoDatePattern.test(dateString)) {
    return dateString;
  }

  const date = moment.utc(dateString, TimeFormat.isoDate);
  const maxDate = moment.utc('9999-12-31', TimeFormat.isoDate);

  if (date.isSameOrAfter(maxDate)) {
    return dateString;
  }

  return date.add(1, 'day').format(TimeFormat.isoDate);
}

/**
 * Formats a number as currency.
 *
 * @param value - Number to format
 * @returns Formatted currency string (e.g., "$1,234.56")
 */
export function formatCurrency(value: number): string {
  return numeral(value).format('$0,0.00');
}

/**
 * Formats a number with thousands separators.
 *
 * @param value - Number to format
 * @returns Formatted number string (e.g., "1,234,567")
 */
export function formatNumber(value: number): string {
  return numeral(value).format('0,0');
}

export function createDateFilters(
  start: string,
  end: string,
): { d_c__gte: string; d_c__lte: string } {
  return {
    d_c__gte: start,
    d_c__lte: addDayToDate(end),
  };
}

export function getPresetStorageKey(modelId: string): string {
  return `${modelId}_conversion_preset`;
}

export function loadPresets(modelId: string): { sort: ISorted[]; columnIds: string[] } | null {
  const presets = sessionStorage.getItem(getPresetStorageKey(modelId));
  if (!presets) {
    return null;
  }
  try {
    return JSON.parse(presets);
  } catch {
    return null;
  }
}

export function savePresets(modelId: string, sort: ISorted[], columnIds: string[]): void {
  sessionStorage.setItem(getPresetStorageKey(modelId), JSON.stringify({ sort, columnIds }));
}
